import { GetStaticProps, GetStaticPaths } from 'next'
import { useInfiniteQuery } from '@tanstack/react-query'

/* blocks */
import Layout from '@/cmp/templates/Layout/BlogLayout'
import BlogCard from '@/molecules/BlogCard/BlogCard'
import BlogPageHero from '@/molecules/BlogPageHero/BlogPageHero'
import PepperCMLCta from '@/cmp/organism/PepperCMLCta/PepperCMLCta'
import BlogNewsletter from '@/cmp/organism/BlogNewsletter/BlogNewsletter'
import CategorySelection from '@/templates/CategorySelection/CategorySelection'
import CMLPromotionalFooter from '@/cmp/organism/CMLPromotionalFooter/CMLPromotionalFooter'
import FooterCta from '@/cmp/organism/FooterCta/FooterCta'

/* utils */
import useUpdateEffect from '@/hooks/useUpdateEffect'
import { fetchListingData } from '@/utils/api/posts'
import { constructBreadcrumbJsonSchema } from '@/utils/functions/jsonSchema'
import { STANDALONE_CATEGORY_PAGES } from '@/utils/enums/categories'
import { capture404PageError } from '@/utils/functions/elk-server'
import { TEN_MINS_REVALIDATION } from '@/utils/constants/revalidation'

/* assets */
import { ReactComponent as LoaderIcon } from 'public/assets/icons/loader.svg'

interface Props {
  data: BlogListingResponse
  categorySlug: string
  category?: BlogPostCategory
}

export default function CategoryListing({
  data,
  categorySlug,
  category
}: Props) {
  useUpdateEffect(() => {
    document
      .querySelector('#js-blog-list')
      ?.scrollIntoView({ behavior: 'smooth' })
  }, [categorySlug])

  const {
    data: blogListingData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['blog posts', data.posts.pageInfo.startCursor, categorySlug],
    ({ pageParam }) =>
      fetchListingData({
        after: pageParam,
        fetchFeaturedPost: false,
        categoryName: categorySlug
      }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      initialData: () => {
        return {
          pageParams: [undefined, 1],
          pages: [data]
        }
      },
      getNextPageParam: lastPage =>
        lastPage.posts.pageInfo.hasNextPage
          ? lastPage.posts.pageInfo.endCursor
          : false
    }
  )

  const postGroup = blogListingData?.pages.map(data => data.posts.nodes) ?? []
  const posts = ([] as BlogPostList[]).concat(...postGroup)

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog/' },
    { name: category?.name ?? '', url: `/blog/category/${category?.slug}` }
  ]

  const metadata = [
    {
      property: 'description',
      content: category?.description ?? ''
    },
    {
      property: 'og:title',
      content: `${category?.name ?? ''} | Pepper Content`
    },
    {
      property: 'og:description',
      content: category?.description ?? ''
    },
    {
      property: 'og:type',
      content: 'website'
    }
  ]

  return (
    <div>
      <Layout
        title={`${category?.name ?? ''} | Pepper Content`}
        meta={metadata}
        canonical={`/blog/category/${category?.slug}/`}
        jsonSchema={[constructBreadcrumbJsonSchema(breadcrumbs)]}
      >
        {category?.name ? (
          <BlogPageHero
            title={category.name}
            description={category.description ?? ''}
            breadcrumbs={breadcrumbs}
          />
        ) : null}

        <section className="container | relative | mx-auto py-5 md:py-9">
          <div
            id="js-blog-list"
            className="grid gap-x-5 gap-y-8 md:gap-y-10 md:gap-x-8 md:grid-cols-12  |  mb-12 lg:mb-18"
          >
            {posts.map(post => (
              <article
                key={post.id}
                className="col-span-1 md:col-span-6 lg:col-span-4 hover:shadow-md transition-all duration-200 ease-in rounded-md"
              >
                <BlogCard
                  type="default"
                  date={post.date}
                  description={post.excerpt}
                  image={
                    post.featuredImage
                      ? {
                          url: post.featuredImage.node.sourceUrl,
                          altText: post.featuredImage.node.altText,
                          width: post.featuredImage.node.mediaDetails.width,
                          height: post.featuredImage.node.mediaDetails.height
                        }
                      : undefined
                  }
                  alt={post.featuredImage?.node.altText}
                  slug={`/blog/${post.slug}/`}
                  categories={post.categories.nodes}
                  readTime={post.readingTime}
                  title={post.title}
                />
              </article>
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-end items-center">
              <hr className="w-full text-blue-web-50" />
              <button
                onClick={() => fetchNextPage()}
                className="text-blue-web-50 font-medium | flex flex-nowrap flex-grow flex-shrink-0 | bg-transparent rounded-md | pl-2 pr-0"
              >
                Load more articles
                {isFetchingNextPage && (
                  <LoaderIcon className="animate-spin | ml-2" />
                )}
              </button>
            </div>
          )}
        </section>

        <PepperCMLCta />

        <BlogNewsletter
          title="Get your hands on the latest news!"
          description="Subscribe to our newsletter and stay informed about the latest trends, industry insights, exciting updates, and exclusive offers delivered directly to your inbox."
          bgColor="#FF6333"
        />
        <CategorySelection />
        <CMLPromotionalFooter />
        <FooterCta
          headline="Ready to pivot your content performance?"
          mode="full_width"
          variant="dark"
          primaryCta={{
            id: '1',
            title: 'Book A Demo',
            href: '/book-a-demo/',
            target: '_blank'
          }}
          secondaryCta={{
            id: '2',
            title: 'Sign Up',
            href: 'https://business.peppercontent.io/login?utm_source=blogs&utm_medium=banner&utm_campaign=blogpagessft',
            target: '_blank'
          }}
        />
      </Layout>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  // redirect to 404 page if standalone category page exists
  const categorySlug = typeof params?.slug === 'string' ? params.slug : ''

  if (STANDALONE_CATEGORY_PAGES.includes(categorySlug)) {
    return {
      redirect: {
        destination: `/${categorySlug}`,
        permanent: true
      }
    }
  }

  const result = await fetchListingData({
    fetchFeaturedPost: false,
    categoryName: categorySlug,
    limit: 9
  })

  if (result.posts.nodes.length === 0) {
    capture404PageError(`/blog/category/${categorySlug}/`)
    return { notFound: true }
  }

  return {
    props: {
      data: result,
      posts: result.posts.nodes,
      categorySlug,
      category: result.category
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}
